import React, { useState, useMemo, useRef, useImperativeHandle, forwardRef } from 'react';
import { ValidationRule, ButtonType } from 'devextreme-react/common';
import { Popup } from 'devextreme-react/popup';
import { Form, Label, FormRef, GroupItem, SimpleItem, ButtonItem } from 'devextreme-react/form';
import { LoadPanel } from 'devextreme-react/load-panel';
import 'devextreme-react/text-area';

import { formatMessage } from 'devextreme/localization';

import ActionType from '../../classes/consts/ActionType';

import LabelTemplate from '../label-template/LabelTemplate';

import CustomerAgentApi from '../../api/CustomerAgentApi';

import AgentCrudLogic from '../../logics/agentmodule/AgentCrudLogic';

import AgentRoleListDto from '../../classes/dtos/agentmodule/AgentRoleListDto';

import AgentFormDto from '../../classes/dtos/agentmodule/AgentFormDto';


interface AgentFormProps {
    AgentAdded(id: number): void;
}

export interface AgentFormHandle {
    Open: (agentId: number | null) => void;
}

const AgentForm = forwardRef<AgentFormHandle, AgentFormProps>(({ AgentAdded }, ref) => {

    const position = { of: '#root' };

    const formRef = useRef<FormRef>(null);

    const [currentAgentId, setCurrentAgentId] = useState<number | null>(null);

    const [agentRoles, setAgentRoles] = useState<AgentRoleListDto[]>([]);

    const [agentCrudLogic] = useState<AgentCrudLogic>(new AgentCrudLogic());

    const [agentFormDto, setAgentFormDto] = useState<AgentFormDto>(new AgentFormDto(null, null));

    const [isOpen, setIsOpen] = useState(false);

    const [action, setAction] = useState<string>("");

    const [saveInProgress, setSaveInProgress] = React.useState(false);

    useImperativeHandle(ref, () => ({
        Open(agentId: number | null) {

            if (agentId === null) {
                setAction(ActionType.Create);
            } else {
                setAction(ActionType.Update);
            }

            setCurrentAgentId(agentId);

            loadAsync(agentId).then(() => {
                setIsOpen(true);
                if (agentId === null) {
                    setAgentFormDto(new AgentFormDto("my agent", null));
                }
            });
        }
    }));

    const loadAsync = async (agentId: number | null): Promise<void> => {

        let agentRoles = [
            new AgentRoleListDto(1, formatMessage("SettingsAgentGridValue_LocalDataSource")),
            new AgentRoleListDto(3, formatMessage("SettingsAgentGridValue_LocalDataSourceAndTaskRunner"))
        ];

        setAgentRoles(agentRoles);

        if (agentId !== null) {
            let getCustomerAgentResult = await CustomerAgentApi.GetCustomerAgentByIdAsync(agentId);
            if (getCustomerAgentResult.IsFailed()) {
                alert("Error in GetCustomerAgentByIdAsync");
            }

            let roles = agentRoles.find(e => e.Id === getCustomerAgentResult.Result.CustomerAgent.Roles) ?? null;

            let data: any = new AgentFormDto(getCustomerAgentResult.Result.CustomerAgent.Name, roles);

            setAgentFormDto(data);

            forceUpdate();
        }
    }

    const agentRolesEditorOptions = {
        items: agentRoles,
        searchEnabled: true,
        displayExpr: "Name",
        readOnly: false,
        onValueChanged: (e: any) => {

            if (!e.event) return;

            let agentRole = e.value as AgentRoleListDto;

            setAgentFormDto({
                ...agentFormDto,
                Roles: agentRole
            });
        }
    };

    const customerAgentNameEditorOptions = {
        valueChangeEvent: 'keyup',
        required: true
    };

    const validationRules: {
        customerAgentName: ValidationRule[],
        customerAgentRoles: ValidationRule[]
    } = {
        customerAgentName: [
            { type: 'required', message: formatMessage("SettingsAgentFormFieldValidation_Name") },
        ],
        customerAgentRoles: [
            { type: 'required', message: formatMessage("SettingsAgentFormFieldValidation_Roles") },
        ]
    };

    const closeDialog = () => {
        setIsOpen(false);
    };

    const popupAttributes = useMemo(() => {
        return {
            id: 'elementId',
            class: 'class-name-popup'
        };
    }, []);

    const handleSubmit = async (e: { preventDefault: () => void; }) => {
        e.preventDefault();

        setSaveInProgress(true);

        if (agentFormDto.Name === null) {
            alert("Name not set");
            setSaveInProgress(false);
            return;
        }

        if (agentFormDto.Roles === null) {
            alert("Roles not set");
            setSaveInProgress(false);
            return;
        }

        let createOrUpdateDataSourceResult = await agentCrudLogic.CreateOrUpdateAgentAsync(currentAgentId, agentFormDto.Name, agentFormDto.Roles.Id);

        if (createOrUpdateDataSourceResult.IsFailed()) {
            alert(createOrUpdateDataSourceResult.ErrorMessage);
            setSaveInProgress(false);
            return;
        }

        AgentAdded(createOrUpdateDataSourceResult.Result.Id);
        setIsOpen(false);
        setSaveInProgress(false);
    };

    const close = () => {
        setIsOpen(false);
    };

    const cancelButtonOptions = {
        text: 'Cancel',
        type: 'outlined' as ButtonType,
        useSubmitBehavior: false,
        width: '100%',
        onClick: close
    };

    const confirmButtonOptions = {
        text: 'Confirm',
        type: 'default' as ButtonType,
        useSubmitBehavior: true,
        width: '100%'
    };

    function getTitle() {
        switch (action) {
            case ActionType.Create: return formatMessage("SettingsAgentForm_TitleCreate");
            case ActionType.Update: return formatMessage("SettingsAgentForm_TitleUpdate");
            default: return "???";
        }
    }

    const [updateTrigger, setUpdateTrigger] = useState(0);

    const forceUpdate = () => setUpdateTrigger(updateTrigger + 1);

    return (
        <React.Fragment>
            <Popup
                wrapperAttr={popupAttributes}
                visible={isOpen}
                onHiding={closeDialog}
                dragEnabled={false}
                hideOnOutsideClick={false}
                showCloseButton={true}
                showTitle={true}
                title={getTitle()}
                container=".dx-viewport"
                width={600}
                height="auto"
            >
                <form onSubmit={handleSubmit}>
                    <Form
                        key={updateTrigger}
                        ref={formRef}
                        formData={agentFormDto}
                        showColonAfterLabel={true}
                        showValidationSummary={false}
                        validationGroup="customerAgentDtoData"
                        colCount={2}
                        disabled={saveInProgress}
                    >
                        <GroupItem colSpan={2}>
                            <SimpleItem dataField="Name" editorOptions={customerAgentNameEditorOptions} validationRules={validationRules.customerAgentName}>
                                <Label text={formatMessage("SettingsAgentFormField_Name")} render={LabelTemplate('rename')} />
                            </SimpleItem>
                            <SimpleItem dataField="Roles" editorType="dxSelectBox" editorOptions={agentRolesEditorOptions} validationRules={validationRules.customerAgentRoles}>
                                <Label text={formatMessage("SettingsAgentFormField_Roles")} render={LabelTemplate('product')} />
                            </SimpleItem>
                        </GroupItem>
                        <GroupItem>
                            <ButtonItem buttonOptions={cancelButtonOptions} />
                        </GroupItem>
                        <GroupItem>
                            <ButtonItem buttonOptions={confirmButtonOptions} />
                        </GroupItem>
                    </Form>
                </form>
            </Popup>
            <LoadPanel
                position={position}
                shadingColor="rgba(0,0,0,0.4)"
                visible={saveInProgress}
            />
        </React.Fragment>
    );
});

export default AgentForm;